import type { Creature } from '@/domain/creature/types';
import { deriveCareTraits, type CareTrait } from './care-traits';
import { normalizeCareProfile } from './profile';
import { resolveBehaviorProfile } from './behavior';
import { deriveTemperament, hasTemperament, type TemperamentProfile } from './temperament';

export interface LifeOutcome {
  careTraits: CareTrait[];
  temperament: TemperamentProfile;
  behaviorTags: string[];
}

export type VisualCareAccent =
  | 'cheek-blush'
  | 'indulgent-crown'
  | 'soft-belly'
  | 'play-spark'
  | 'mess-smudge'
  | 'discipline-band'
  | 'rebel-spikes'
  | 'recovery-spark'
  | 'frail-haze';

export function deriveLifeOutcome(creature: Creature): LifeOutcome {
  const careTraits = deriveCareTraits(creature);
  const temperament = deriveTemperament(creature.genome);
  const behavior = resolveBehaviorProfile(creature);
  const care = normalizeCareProfile(creature.careProfile);
  const tags = new Set<string>(behavior.behaviorTags);

  if (hasTemperament(temperament, 'glutton') && care.snacks >= care.healthyMeals + care.heartyMeals + 3) {
    tags.add('glutton-spoiled');
  }
  if (hasTemperament(temperament, 'fragile') && care.heals > 0) {
    tags.add('fragile-resilient');
  }
  if (hasTemperament(temperament, 'tidy') && care.lateCleanups >= 2) {
    tags.add('tidy-neglected');
  }
  if (hasTemperament(temperament, 'playful') && care.plays >= 4) {
    tags.add('playful-bonded');
  }
  if (hasTemperament(temperament, 'stubborn') && care.correctDisciplines >= 2) {
    tags.add('stubborn-respected');
  }

  return { careTraits, temperament, behaviorTags: [...tags] };
}

export function deriveVisualCareAccents(creature: Creature): VisualCareAccent[] {
  const outcome = deriveLifeOutcome(creature);
  const lifecycleTraits = (creature.lifecycle.careTraits ?? []) as CareTrait[];
  const traits = new Set<CareTrait>([...lifecycleTraits, ...outcome.careTraits]);
  const tags = new Set(outcome.behaviorTags);
  const accents: VisualCareAccent[] = [];

  if (traits.has('well-fed')) accents.push('cheek-blush');
  if (traits.has('spoiled') || tags.has('glutton-spoiled')) accents.push('indulgent-crown');
  if (traits.has('heavy') || tags.has('glutton-spoiled')) accents.push('soft-belly');
  if (traits.has('playful') || tags.has('playful-bonded')) accents.push('play-spark');
  if (traits.has('messy') || tags.has('tidy-neglected')) accents.push('mess-smudge');
  if (traits.has('disciplined') || tags.has('stubborn-respected')) accents.push('discipline-band');
  if (traits.has('rebellious')) accents.push('rebel-spikes');
  if (traits.has('resilient') || tags.has('fragile-resilient')) accents.push('recovery-spark');
  if (traits.has('frail')) accents.push('frail-haze');

  return [...new Set(accents)];
}
